import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { sentry } from 'sentry';

@Injectable({
  providedIn: 'root'
})
export class SentryInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const queryParams = request.params.toString();
    const body = request.body;

    return next.handle(request).pipe(
      tap({
        error: (err) => {
          if (
            isAuthErrorStatus(err.status) ||
            hasUnauthenticatedErrorCode(err.error)
          ) {
            return;
          }

          sentry.setContext('HTTP Request', {
            url: request.url,
            method: request.method,
            queryParams,
            body: sanitizeBody(body),
          });

          const errors =
            (err.error &&
              err.error.errors &&
              Array.isArray(err.error.errors)
                ? err.error.errors.map((error) => error.message).join(' // ')
                : null) ||
            (err.error && err.error.message) ||
            'Unknown error';

          sentry.setContext('HTTP Response', {
            status: err.status,
            message: err.message,
            errors: errors
          });

          sentry.captureException(err);
        }
      })
    );
  }
}

function isAuthErrorStatus(status: number): boolean {
  return status === 401;
}

function hasUnauthenticatedErrorCode(body: any): boolean {
  return body && Array.isArray(body.errors) && body.errors
    .filter((error: any) => error.extensions)
    .some((error: any) => error.extensions.code === 'UNAUTHENTICATED');
}

function sanitizeBody(body: any): any {
  if (!body) { return body; }

  const sensitiveKeys = ['password', 'token', 'authorization', 'secret'];

  function deepSanitize(obj: any, seen = new WeakSet()): any {
    if (obj === null || obj === undefined) { return obj; }

    if (typeof obj !== 'object') { return obj; }

    if (seen.has(obj)) { return obj; }
    seen.add(obj);

    if (Array.isArray(obj)) {
      return obj.map((item) => deepSanitize(item, seen));
    }

    const clone: any = {};

    for (const key of Object.keys(obj)) {
      if (sensitiveKeys.includes(key.toLowerCase())) {
        clone[key] = '***';
      } else {
        clone[key] = deepSanitize(obj[key], seen);
      }
    }

    return clone;
  }

  return deepSanitize(body);
}

